import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import IconButton from 'components/@extended/IconButton';
import { getUnreadNotifications, markNotificationRead } from 'api/push';

// assets
import { IconNotifications, IconClose } from 'utils/icons';

// ==============================|| HEADER CONTENT - NOTIFICATION (OFFCANVAS-RIGHT-MESSAGE) ||============================== //
// Push modul Fáze 3 (2026-08-22) - dřív holý stub (notificationCount vždy
// 0). Teď čte skutečná nepřečtená in-app oznámení (push_notifications,
// GET /push/notifications/unread - stejný záznam, co PushService::
// sendToUser() zapisuje VŽDY před pokusem o skutečné WebPush doručení,
// takže tohle je spolehlivý kanál i když OS push selže/je vypnutý). Kliknutí
// na položku ji označí přečtenou (POST /push/notifications/{id}/read) a
// zmizí ze seznamu. Bell ukazuje přeškrtnutou ikonu, když je počet 0
// (žádná ikonová sada nemá přesně "bell-slash", je to CSS overlay čára).

function BellIcon({ crossed }) {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', lineHeight: 0, color: crossed ? 'text.disabled' : 'inherit' }}>
      <IconNotifications style={{ fontSize: '1.65rem' }} />
      {crossed && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '140%',
            height: '2px',
            bgcolor: 'currentColor',
            transform: 'translate(-50%, -50%) rotate(-45deg)'
          }}
        />
      )}
    </Box>
  );
}

function formatDateTime(value) {
  if (!value) return '';
  try {
    return new Date(value.replace(' ', 'T')).toLocaleString('cs-CZ');
  } catch {
    return value;
  }
}

// 60s - lehký polling, ne WebSocket/SSE (žádný takový kanál v appce
// zatím neexistuje) - stačí na to, aby se odznak časem sám doplnil, i
// když uživatel drawer neotevře.
const POLL_INTERVAL_MS = 60000;

export default function Notification() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = () => {
    getUnreadNotifications()
      .then(setNotifications)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // Prohlížeč silně omezuje setInterval na neaktivní/pozadové záložce
  // (2026-08-23, bug - "objevi se teprve po otevreni a zavreni centra
  // zprav... pokud je uzivatel na strankach") - 60s interval samo o sobě
  // v aktivní záložce funguje, ale když appka běží na pozadí (jiná záložka,
  // minimalizované okno), prohlížeč timer zpomalí/pozastaví a jediné, co pak
  // reálně obnoví seznam, je ruční otevření panelu (handleToggle volá load()
  // napřímo). Doplněn visibilitychange listener, který natáhne čerstvá data
  // hned, jakmile se záložka znovu stane aktivní, nezávisle na intervalu.
  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_INTERVAL_MS);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  const handleToggle = () => {
    setOpen(true);
    load();
  };
  const handleClose = () => setOpen(false);

  // Odkaz po kliknutí (2026-08-23, bug - dřív se "Odkaz po kliknutí" u
  // odeslané zprávy uplatnil jen u skutečné OS push notifikace/sw.js's
  // notificationclick, ne tady v in-app centru zpráv) - n.url je uložené ve
  // stejném formátu jako sw.js dostává (/app/... - viz Push.jsx's
  // flattenMenuLinks()), takže se tu jen odstraní "/app" prefix, o který se
  // react-routeru stará jeho vlastní basename (VITE_APP_BASE_NAME).
  const handleItemClick = async (n) => {
    setNotifications((prev) => prev.filter((item) => item.id !== n.id));
    if (n.url) {
      const path = n.url.startsWith('/app/') ? n.url.slice(4) : n.url;
      navigate(path);
      setOpen(false);
    }
    try {
      await markNotificationRead(n.id);
    } catch {
      load();
    }
  };

  const notificationCount = notifications.length;

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <Tooltip title="Oznámení" disableInteractive>
        <IconButton
          color="secondary"
          variant="light"
          sx={{ color: 'text.primary', bgcolor: open ? 'grey.100' : 'transparent' }}
          aria-label="oznámení"
          aria-controls={open ? 'offcanvas-right-message' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Badge badgeContent={notificationCount} color="primary">
            <BellIcon crossed={notificationCount === 0} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Drawer
        id="offcanvas-right-message"
        anchor="right"
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 320 } } } }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5">Oznámení</Typography>
          <IconButton onClick={handleClose} aria-label="zavřít">
            <IconClose style={{ fontSize: '1.4rem' }} />
          </IconButton>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Zatím žádná oznámení.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((n) => (
              <ListItemButton key={n.id} divider onClick={() => handleItemClick(n)} sx={{ px: 2.5, py: 1.5, alignItems: 'flex-start' }}>
                <ListItemText
                  primary={n.title}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                        {n.body}
                      </Typography>
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatDateTime(n.created_at)}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Drawer>
    </Box>
  );
}
